import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Logo } from "@/components/site/Logo";
import { useLang } from "@/context/LanguageContext";
import { scrollToId } from "@/lib/scroll";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Globe, Check } from "lucide-react";

const LANG_LABEL = { it: "Italiano", en: "English", nl: "Nederlands" };

export const Header = ({ onBook }) => {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => scrollToId(id);

  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-ivy/80 backdrop-blur-xl border-b border-gold/15" : "bg-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
        <button
          onClick={() => scrollTo("hero")}
          className="flex items-center gap-3 group"
          data-testid="logo-home-button"
        >
          <Logo className="text-gold transition-transform duration-500 group-hover:rotate-[10deg]" size={34} />
          <span className="font-serifluxe text-sand text-sm sm:text-base tracking-[0.22em] uppercase">
            {t.brand}
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-9 font-body text-sm tracking-wide text-sand/80">
          <button onClick={() => scrollTo("about")} className="hover:text-gold transition-colors" data-testid="nav-about">{t.nav.about}</button>
          <button onClick={() => scrollTo("readings")} className="hover:text-gold transition-colors" data-testid="nav-readings">{t.nav.readings}</button>
          <button onClick={() => scrollTo("guide")} className="hover:text-gold transition-colors" data-testid="nav-guide">{t.nav.guide}</button>
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="flex items-center gap-1.5 text-sand/80 hover:text-gold transition-colors text-sm uppercase tracking-widest"
                data-testid="lang-switcher"
              >
                <Globe size={16} />
                {lang}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-espresso border-gold/25 text-sand">
              {Object.keys(LANG_LABEL).map((l) => (
                <DropdownMenuItem
                  key={l}
                  onClick={() => setLang(l)}
                  className="cursor-pointer focus:bg-gold/15 focus:text-gold flex items-center justify-between"
                  data-testid={`lang-option-${l}`}
                >
                  {LANG_LABEL[l]}
                  {lang === l && <Check size={14} className="text-gold" />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <Button
            onClick={onBook}
            className="rounded-full bg-rust hover:bg-rust/90 text-cream font-body tracking-wide px-5 h-9 border border-rust/40 transition-transform hover:scale-[0.97]"
            data-testid="header-book-button"
          >
            {t.nav.book}
          </Button>
        </div>
      </div>
    </motion.header>
  );
};

export default Header;
